import { memo, useEffect, useRef } from 'react';

import { isReactNativeWebView } from '@utils/deviceInfo';
import { softReloadApp } from '@utils/appLifecycle';
import { warnIfSendPending } from '@utils/pendingSendGuard';

import './floatingReloadButton.scss';

const STORAGE_KEY = 'floating-reload-button:position';
const BUTTON_SIZE = 52;
const EDGE_MARGIN = 16;
const DRAG_THRESHOLD = 6;

type Position = { x: number; y: number };

type DragState = {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPosX: number;
    startPosY: number;
    moved: boolean;
};

function clampPosition(pos: Position): Position {
    const maxX = Math.max(EDGE_MARGIN, window.innerWidth - BUTTON_SIZE - EDGE_MARGIN);
    const maxY = Math.max(EDGE_MARGIN, window.innerHeight - BUTTON_SIZE - EDGE_MARGIN);

    return {
        x: Math.min(Math.max(pos.x, EDGE_MARGIN), maxX),
        y: Math.min(Math.max(pos.y, EDGE_MARGIN), maxY),
    };
}

function getDefaultPosition(): Position {
    return {
        x: window.innerWidth - BUTTON_SIZE - EDGE_MARGIN,
        y: window.innerHeight - BUTTON_SIZE - EDGE_MARGIN * 4,
    };
}

function readStoredPosition(): Position {
    if (typeof window === 'undefined') {
        return { x: EDGE_MARGIN, y: EDGE_MARGIN };
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
                return clampPosition(parsed);
            }
        }
    } catch (error) {
        console.log(error);
    }

    return clampPosition(getDefaultPosition());
}

function savePosition(pos: Position): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch (error) {
        console.log(error);
    }
}

function applyPosition(el: HTMLButtonElement | null, pos: Position): void {
    if (!el) {
        return;
    }
    el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
}

/**
 * Плавающая кнопка перезагрузки страницы. Видна на всех страницах сайта,
 * позицию можно перетащить — она сохраняется в localStorage.
 * Перетаскивание не создаёт React-рендеров: позиция во время драга
 * применяется напрямую к DOM-узлу через ref.
 */
const FloatingReloadButton = memo((): JSX.Element => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const positionRef = useRef<Position | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    const justDraggedRef = useRef(false);

    if (positionRef.current === null) {
        positionRef.current = readStoredPosition();
    }

    useEffect(() => {
        const handleResize = () => {
            const clamped = clampPosition(positionRef.current as Position);
            positionRef.current = clamped;
            applyPosition(buttonRef.current, clamped);
            savePosition(clamped);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    function finishDrag(pointerId: number) {
        const drag = dragStateRef.current;
        if (!drag || drag.pointerId !== pointerId) {
            return;
        }

        buttonRef.current?.releasePointerCapture(pointerId);
        buttonRef.current?.classList.remove('floating-reload-button--dragging');

        if (drag.moved) {
            savePosition(positionRef.current as Position);
        }
        justDraggedRef.current = drag.moved;
        dragStateRef.current = null;
    }

    function handlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }

        buttonRef.current?.setPointerCapture(e.pointerId);
        dragStateRef.current = {
            pointerId: e.pointerId,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startPosX: (positionRef.current as Position).x,
            startPosY: (positionRef.current as Position).y,
            moved: false,
        };
    }

    function handlePointerMove(e: React.PointerEvent<HTMLButtonElement>) {
        const drag = dragStateRef.current;
        if (!drag || drag.pointerId !== e.pointerId) {
            return;
        }

        const dx = e.clientX - drag.startClientX;
        const dy = e.clientY - drag.startClientY;

        if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
            return;
        }

        if (!drag.moved) {
            drag.moved = true;
            buttonRef.current?.classList.add('floating-reload-button--dragging');
        }

        const next = clampPosition({ x: drag.startPosX + dx, y: drag.startPosY + dy });
        positionRef.current = next;
        applyPosition(buttonRef.current, next);
    }

    function handlePointerUp(e: React.PointerEvent<HTMLButtonElement>) {
        finishDrag(e.pointerId);
    }

    function handlePointerCancel(e: React.PointerEvent<HTMLButtonElement>) {
        finishDrag(e.pointerId);
    }

    function handleClick() {
        if (justDraggedRef.current) {
            justDraggedRef.current = false;
            return;
        }

        // Перезагрузка оборвала бы отправку сообщения, идущую в фоне
        if (warnIfSendPending()) return;

        if (isReactNativeWebView()) {
            // Хост-приложение перехватывает любую полную навигацию (включая
            // window.location.reload()) как переход по внешней ссылке и
            // открывает её в системном браузере вместо перезагрузки внутри
            // WebView. Поэтому здесь не трогаем window.location вообще —
            // делаем ремаунт SPA-дерева и сброс кэша react-query силами
            // самого приложения. Сигнал хосту шлём отдельно best-effort —
            // на случай, если он умеет сделать нативный webViewRef.reload().
            const rnWebView = (window as unknown as { ReactNativeWebView?: { postMessage: (message: string) => void } })
                .ReactNativeWebView;
            rnWebView?.postMessage(JSON.stringify({ type: 'RELOAD_PAGE' }));

            softReloadApp();
            return;
        }

        window.location.reload();
    }

    return (
        <button
            type="button"
            ref={buttonRef}
            className="floating-reload-button"
            style={{ transform: `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)` }}
            title="Обновить страницу"
            aria-label="Обновить страницу"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onClick={handleClick}
        >
            <svg
                className="floating-reload-button__icon"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                aria-hidden="true"
            >
                <path
                    d="M4.5 12a7.5 7.5 0 0 1 12.62-5.5M19.5 12a7.5 7.5 0 0 1-12.62 5.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
                <path
                    d="M17.5 3.5v4h-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M6.5 20.5v-4h4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
});

export { FloatingReloadButton };
